import { useCallback, useEffect, useState } from "react";
import { Search, Filter, ScrollText, Eye, Download } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { PaginationBar, EmptyState, LoadingBlock } from "@/components/Pagination";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const PAGE_SIZE = 25;

const ACTIONS = [
  "login_success", "login_failed", "register", "logout",
  "profile_updated", "password_changed", "password_reset",
  "admin_user_created", "admin_user_updated", "admin_user_status_changed",
  "admin_password_reset", "admin_settings_updated", "admin_cms_updated",
];

const MODULES = ["auth", "users", "admin", "audit"];

const MODULE_COLOR = {
  auth: "bg-blue-50 text-[#101876]",
  users: "bg-emerald-50 text-emerald-700",
  admin: "bg-violet-50 text-violet-700",
  audit: "bg-slate-100 text-slate-700",
};

export default function AdminAuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("all");
  const [module, setModule] = useState("all");
  const [ip, setIp] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (action !== "all") params.action = action;
      if (module !== "all") params.module = module;
      if (ip) params.ip_address = ip;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo + "T23:59:59").toISOString();
      const { data } = await api.get("/admin/audit-logs", { params });
      setLogs(data.data.logs);
      setTotal(data.data.total);
    } finally {
      setLoading(false);
    }
  }, [page, search, action, module, ip, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout title="Audit Logs" breadcrumbs={["Platform", "Security", "Audit Logs"]}>
      <div className="space-y-4" data-testid={ADMIN.auditRoot}>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder="Search description or action…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid={ADMIN.auditSearch} />
            </div>
            <Select value={action} onValueChange={(v) => { setAction(v); setPage(1); }}>
              <SelectTrigger className="w-48" data-testid={ADMIN.auditActionFilter}><SelectValue placeholder="Action" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={module} onValueChange={(v) => { setModule(v); setPage(1); }}>
              <SelectTrigger className="w-36" data-testid={ADMIN.auditModuleFilter}><SelectValue placeholder="Module" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modules</SelectItem>
                {MODULES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input className="w-32" placeholder="IP address" value={ip} onChange={(e) => { setIp(e.target.value); setPage(1); }} />
            <Input type="date" className="w-40" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} data-testid={ADMIN.auditDateFrom} />
            <Input type="date" className="w-40" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} data-testid={ADMIN.auditDateTo} />
            <button
              onClick={() => toast.info("Export unlocks in Phase 9.")}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-dashed border-[#329112]/40 bg-[#329112]/5 px-3 py-1.5 text-xs font-semibold text-[#329112]"
              data-testid="audit-export-btn"
            >
              <Download className="h-3.5 w-3.5" /> Export (Phase 9)
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white">
          {loading ? (
            <LoadingBlock label="Loading logs…" />
          ) : logs.length === 0 ? (
            <EmptyState title="No audit logs match" description="Try clearing filters or widening the date range." icon={Filter} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Time</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">User</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Role</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Action</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Module</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Description</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">IP</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((l) => (
                  <TableRow key={l.id} data-testid={ADMIN.auditRow(l.id)}>
                    <TableCell className="whitespace-nowrap text-xs text-slate-500">{new Date(l.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-sm text-slate-900">{l.user?.full_name || l.user?.email || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{l.user?.role_code || "—"}</TableCell>
                    <TableCell className="font-medium text-slate-900">{l.action}</TableCell>
                    <TableCell><Badge className={`${MODULE_COLOR[l.module] || "bg-slate-100 text-slate-700"} hover:bg-slate-100`}>{l.module}</Badge></TableCell>
                    <TableCell className="max-w-md truncate text-sm text-slate-600">{l.description}</TableCell>
                    <TableCell className="text-xs text-slate-500">{l.ip_address || "—"}</TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => setDetail(l)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]">
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testKey="audit-pagination" />
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Audit log detail</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              {[
                ["Time", new Date(detail.created_at).toLocaleString()],
                ["User", detail.user?.full_name || "—"],
                ["Role", detail.user?.role_code || "—"],
                ["Action", detail.action],
                ["Module", detail.module],
                ["Description", detail.description],
                ["IP address", detail.ip_address || "—"],
                ["User agent", detail.user_agent || "—"],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{k}</div>
                  <div className="text-sm text-slate-900 break-all">{v}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
